/**
 * AseoTarjetasMovil — los aseos de la ASEADORA como tarjetas en celular
 * (<= 576 px). Round 2026-10-26.
 *
 * Por qué: la tabla de la aseadora mide ~1300 px de ancho. En 412 px el botón
 * «Notas» quedaba fuera de pantalla y había que desplazar de costado para
 * encontrarlo. En tarjetas todo cabe: fecha y hora, estado, propiedad
 * (MAYÚSCULAS), pago, tareas, imágenes y un botón «Notas» de >= 44 px.
 *
 * NO duplica la lógica: cada dato se pinta con el `render` de la columna
 * correspondiente de `buildAseosColumns` (la misma que usa la tabla), incluidas
 * las acciones (Notas / Tomar aseo / Asignado). Solo cambia la disposición.
 * El admin conserva la tabla; este componente solo lo monta el rol aseador.
 *
 * `data-testid`: aseo-tarjeta (con data-aseo-id), aseo-tarjetas-vacio,
 * aseo-tarjetas-mas.
 */

import { useState, type ReactNode } from "react";
import { Button, Empty, Spin } from "antd";
import "../../../Css/aseos-movil.css";

/** Tarjetas que se muestran de entrada y cuántas más trae «Ver más». */
export const TARJETAS_POR_PAGINA = 10;

/** Elementos dentro de la tarjeta que tienen su propia acción: tocarlos no abre las notas. */
const SELECTOR_INTERACTIVO =
  "button, a, input, .ant-btn, .ant-checkbox, .ant-tag[style*='cursor: pointer'], [role='button']";

interface Props {
  data: any[];
  /** Salida de `buildAseosColumns` (sin filtrar): se busca cada columna por su `key`. */
  columns: any[];
  loading?: boolean;
  /** Texto cuando no hay aseos en esta pestaña. */
  vacio: string;
  /** Tocar la tarjeta (fuera de sus botones) abre las notas del aseo. */
  onAbrir: (record: any) => void;
  /** Oculta la columna Acciones (pestaña «terminados»: la tabla tampoco la muestra). */
  sinAcciones?: boolean;
}

/** Pinta una celda con el `render` de su columna, con los mismos argumentos que antd. */
function celda(columns: any[], key: string, record: any): ReactNode {
  const col = columns.find((c) => c.key === key);
  if (!col) return null;
  const valor = col.dataIndex ? record?.[col.dataIndex] : record;
  return col.render ? col.render(valor, record) : valor;
}

export default function AseoTarjetasMovil({
  data,
  columns,
  loading = false,
  vacio,
  onAbrir,
  sinAcciones = false,
}: Props) {
  const [visibles, setVisibles] = useState(TARJETAS_POR_PAGINA);

  if (loading && data.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 24 }}>
        <Spin />
      </div>
    );
  }
  if (data.length === 0) {
    return (
      <Empty
        data-testid="aseo-tarjetas-vacio"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={vacio}
      />
    );
  }

  const mostrados = data.slice(0, visibles);
  const faltan = data.length - mostrados.length;

  return (
    <div className="aseo-tarjetas" data-testid="aseo-tarjetas">
      {mostrados.map((record) => (
        <article
          key={record.id}
          className="aseo-tarjeta"
          data-testid="aseo-tarjeta"
          data-aseo-id={record.id}
          onClick={(e) => {
            if ((e.target as HTMLElement).closest(SELECTOR_INTERACTIVO)) return;
            onAbrir(record);
          }}
        >
          <header className="aseo-tarjeta-cabecera">
            <div className="aseo-tarjeta-cuando">
              <span className="aseo-tarjeta-fecha">
                {celda(columns, "fechaInicio", record)}
              </span>
              <span className="aseo-tarjeta-hora">
                {celda(columns, "horario", record)}
              </span>
            </div>
            <div className="aseo-tarjeta-estado">{celda(columns, "estado", record)}</div>
          </header>

          <div className="aseo-tarjeta-propiedad">
            {celda(columns, "propiedad", record)}
          </div>

          <div className="aseo-tarjeta-codigos">
            {celda(columns, "aseoCode", record)}
            {celda(columns, "estadia", record)}
          </div>

          <dl className="aseo-tarjeta-datos">
            <div>
              <dt>Pago</dt>
              <dd>{celda(columns, "sueldoAseador", record)}</dd>
            </div>
            <div>
              <dt>Tareas</dt>
              <dd>{celda(columns, "tareas", record)}</dd>
            </div>
            <div>
              <dt>Imágenes</dt>
              <dd>{celda(columns, "imagenes", record)}</dd>
            </div>
          </dl>

          {!sinAcciones && (
            <footer className="aseo-tarjeta-acciones">
              {celda(columns, "acciones", record)}
            </footer>
          )}
        </article>
      ))}

      {faltan > 0 && (
        <Button
          block
          size="large"
          className="aseo-tarjetas-mas"
          data-testid="aseo-tarjetas-mas"
          onClick={() => setVisibles((v) => v + TARJETAS_POR_PAGINA)}
        >
          Ver más ({faltan})
        </Button>
      )}
    </div>
  );
}
